<div class="header-container page-header" [class.page-header--compact]="compact">
    <div class="header-row">
        <!-- LEFT HEADER -->
        <!-- Grid layout: chip occupies col 1 row 1 (vertically centered on the title row);
    title / description / dropdowns occupy col 2 rows 1-3 so they share an indent.
    When no chip is projected, col 1 collapses (max-content with no content = 0)
    and col 2 sits flush-left exactly as before — no regression for other pages. -->
        <div class="header-grid">
            <ng-content select="[breadcrumb]"></ng-content>
            <h1 class="page-header__title hg-title">
                {{ title }}
            </h1>
            @if (description && !compact) {
                <p class="page-header__description hg-description">
                    {{ description }}
                </p>
            }
            <div class="flex flex-wrap align-items-center gap-2 hg-dropdowns">
                <!-- ENTITY SELECTION -->
                @if (selectionMode === 'single' && entityOptions?.length) {
                    <p-select
                        [options]="entityOptions"
                        [(ngModel)]="selectedEntity"
                        (onChange)="handleEntityChange()"
                        optionLabel="name"
                        [placeholder]="'components.header.entity.placeholder' | translate"
                        [ariaLabel]="'components.header.entity.placeholder' | translate"
                        class="header-picker"
                        data-testid="single-entity-dropdown-header"></p-select>
                }

                @if (selectionMode === 'multiple') {
                    <p-multiSelect
                        [options]="entityOptions"
                        [(ngModel)]="selectedEntities"
                        (onPanelHide)="handleEntityChange()"
                        optionLabel="name"
                        display="chip"
                        [placeholder]="'components.header.entity.placeholder' | translate"
                        [ariaLabel]="'components.header.entity.placeholder' | translate"
                        class="header-picker header-picker--auto"
                        [filterPlaceHolder]="'common.search' | translate">
                        <!-- The first two entities, then a count of the others. -->
                        <ng-template pTemplate="selectedItems">
                            @if (selectedEntities && selectedEntities.length > 0) {
                                @for (item of selectedEntities | slice: 0 : 2; track item) {
                                    <p-chip>
                                        <span class="chip-label">{{ item.name }}</span>
                                    </p-chip>
                                }
                                @if (selectedEntities.length > 2) {
                                    <p-chip [label]="'+' + (selectedEntities.length - 2)" />
                                }
                            }
                        </ng-template>
                    </p-multiSelect>
                }

                <!-- YEAR SELECTION -->
                @if (yearOptions?.length) {
                    <p-select
                        [options]="yearOptions"
                        [(ngModel)]="selectedYear"
                        (onChange)="handleYearChange()"
                        [placeholder]="'components.header.year.placeholder' | translate"
                        [ariaLabel]="'components.header.year.placeholder' | translate"
                        class="header-picker header-picker--auto"></p-select>
                }

                <!-- Tag Selection -->
                @if (selectionMode === 'multiple') {
                    <div class="tag-selection">
                        <p-treeSelect
                            [options]="tagsTreeOptions"
                            [(ngModel)]="selectedTags"
                            selectionMode="multiple"
                            [metaKeySelection]="false"
                            [placeholder]="'advancedMeasure.form.placeholder.tag' | translate"
                            [ariaLabel]="'advancedMeasure.form.placeholder.tag' | translate"
                            appendTo="body"
                            panelStyleClass="tag-filter-panel"
                            (onHide)="handleTagChange()"
                            [disabled]="!anyExpertData"
                            class="header-picker header-picker--auto header-picker--label">
                            <ng-template pTemplate="value">
                                <span>{{ 'components.header.tag.selected.' + selectedTagFilterMode | translate }}</span>
                            </ng-template>
                            <ng-template pTemplate="header">
                                <div class="form-choices tag-filter-modes">
                                    @for (tagFilterMode of tagFilterModeOptions; track tagFilterMode) {
                                        <div class="form-choice">
                                            <p-radioButton
                                                [inputId]="'header-tag-filter-' + tagFilterMode"
                                                name="tagFilterMode"
                                                [value]="tagFilterMode"
                                                [(ngModel)]="selectedTagFilterMode" />
                                            <label [for]="'header-tag-filter-' + tagFilterMode">{{
                                                'components.header.tag.' + tagFilterMode | translate
                                            }}</label>
                                        </div>
                                    }
                                </div>
                            </ng-template>
                        </p-treeSelect>
                        @if (selectedTags && selectedTags.length > 0) {
                            <div class="tag-chips">
                                @for (tag of selectedTags | slice: 0 : 2; track tag) {
                                    <p-chip [removable]="true" (onRemove)="removeTag(tag)">
                                        <span class="chip-label">{{ tag.label }}</span>
                                    </p-chip>
                                }
                                @if (selectedTags.length > 2) {
                                    <p-chip
                                        [label]="'+' + (selectedTags.length - 2)"
                                        [pTooltip]="getTagTooltipText()" />
                                }
                            </div>
                        }
                    </div>
                }

                <!-- FILTER SELECTION -->
                @if (filtersOptions?.length) {
                    <p-multiSelect
                        [options]="filtersOptions"
                        [(ngModel)]="selectedFilters"
                        placeholder=""
                        class="header-picker"
                        [filterPlaceHolder]="'common.search' | translate"></p-multiSelect>
                }
                <!-- ORGANIZATION SELECTION -->
                @if (dedicatedAdminOptions?.length) {
                    <p-select
                        [options]="dedicatedAdminOptions"
                        [(ngModel)]="selectedDedicatedAdmin"
                        (onChange)="
                            dedicatedAdminChange.emit($event); selectedDedicatedAdminChange.emit(selectedDedicatedAdmin)
                        "
                        optionLabel="fullname"
                        [disabled]="dedicatedAdminDisabled"
                        [placeholder]="'admin.selected_admin' | translate"
                        [ariaLabel]="'admin.selected_admin' | translate"
                        class="header-picker">
                    </p-select>
                }
            </div>
        </div>
        <!-- RIGHT HEADER: the page's own actions (projected with `headerActions`), then the header's tools -->
        <div class="page-header__actions">
            <ng-content select="[headerActions]"></ng-content>
            <!-- MODAL ICON (measure & solution) -->
            @if (showQuestionnaireIcon) {
                <p-button
                    icon="ms ms-help"
                    severity="secondary"
                    [text]="true"
                    [rounded]="true"
                    [ariaLabel]="'components.header.help' | translate"
                    [pTooltip]="'components.header.help' | translate"
                    tooltipPosition="left"
                    (onClick)="onOpenModalClick()" />
            }
            <!-- KNOB (CSRD) -->
            @if (knobValue !== undefined) {
                <div class="flex flex-column align-items-center justify-content-center">
                    <p-knob [(ngModel)]="knobValue" [size]="60" [strokeWidth]="10" [readonly]="true"></p-knob>
                    <span class="text-sm">
                        {{ 'header.knob' | translate }}
                    </span>
                </div>
            }
        </div>
    </div>
</div>
